// ============================================================================
// transferService.js — Inventory Transfer business logic (single source).
// ============================================================================
// Reuses the existing, centralized approval-limit pattern from
// approvalLimits.js (already used identically by PurchaseOrders.jsx and
// Subcontracts.jsx) -- per the approved decision, no new approval system.
// ============================================================================

import { supabase } from '../lib/supabase'
import { buildInventory, buildMaterialsPolicyMap } from '../lib/inventorySSOT'

/**
 * Generates the next sequential transfer number, e.g. TR-000001.
 * Mirrors the PO-number generation pattern already used elsewhere in the
 * project (sequential, human-readable document numbers).
 */
export async function generateTransferNo() {
  const { count } = await supabase.from('inventory_transfers').select('id', { count: 'exact', head: true })
  const next = (count || 0) + 1
  return `TR-${String(next).padStart(6, '0')}`
}

/**
 * Fetches everything needed to compute a project's current inventory,
 * INCLUDING already-approved transfers affecting it (both as source and
 * destination) -- the same three-source model documented in inventorySSOT.js.
 */
export async function loadProjectInventoryContext(projectId) {
  const [receiptsRes, issuesRes, materialsRes, transferOutRes, transferInRes] = await Promise.all([
    supabase.from('store_receipts').select('ref_code,inventory_ref,material,unit,qty_received,total_exc_vat').eq('project_id', projectId),
    supabase.from('store_issues').select('ref_code,inventory_ref,material,unit,qty_issued').eq('project_id', projectId),
    supabase.from('materials').select('material_code,threshold_type,critical_level,reorder_level').eq('project_id', projectId),
    // Transfer Out (2026-08-13 correction): fires at 'approved' AND 'received' --
    // the source loses the material the moment approval happens (it has
    // physically left the source warehouse at that point), independent of
    // whether the destination has confirmed receipt yet.
    supabase.from('inventory_transfer_lines')
      .select('ref_code,material,unit,qty,source_wac,inventory_transfers!inner(from_project_id,status)')
      .eq('inventory_transfers.from_project_id', projectId)
      .in('inventory_transfers.status', ['approved', 'received']),
    // Transfer In (2026-08-13 correction): fires ONLY at 'received' -- the
    // destination gains nothing on approval alone. Material is "in transit"
    // between approval and the destination storekeeper's explicit receipt
    // confirmation (receiveTransfer() below), matching the requested
    // three-role workflow: warehouse creates -> cost_control approves ->
    // destination warehouse receives.
    supabase.from('inventory_transfer_lines')
      .select('ref_code,material,unit,qty,source_wac,inventory_transfers!inner(to_project_id,status)')
      .eq('inventory_transfers.to_project_id', projectId)
      .eq('inventory_transfers.status', 'received'),
  ])

  const materialsPolicyMap = buildMaterialsPolicyMap(materialsRes.data || [])
  const inventory = buildInventory(
    receiptsRes.data || [],
    issuesRes.data || [],
    materialsPolicyMap,
    transferOutRes.data || [],
    transferInRes.data || []
  )
  return { inventory, materialsPolicyMap, raw: { receiptsRes, issuesRes, materialsRes, transferOutRes, transferInRes } }
}

/**
 * Submits a Draft transfer for Cost Control approval. Single-stage only --
 * per correction (2026-08-13): Transfer has ONE approval role (Cost
 * Control), never escalates to Director regardless of value. This is
 * simpler than PO/Subcontracts' two-stage threshold model on purpose --
 * an inventory transfer is not a new financial commitment (it stays
 * Cost-Neutral per the approved decision), so the PO/Subcontracts director
 * escalation pattern does not apply here.
 */
export async function submitTransfer(transferId) {
  const { error } = await supabase.from('inventory_transfers')
    .update({ approval_stage: 'cost_control', status: 'cost_control', updated_at: new Date().toISOString() })
    .eq('id', transferId)
  return { error }
}

/**
 * Approval action. Single-stage: Cost Control approval is always final --
 * no director escalation (corrected 2026-08-13; the original PO/Subcontracts
 * two-stage threshold pattern was mistakenly copied and does not apply to
 * Transfer). Reused only from the existing role tier, not a new permission.
 *
 * CRITICAL (per approved decision): this function performs the Hard Block
 * insufficient-stock check and the WAC capture AT THIS MOMENT, not at
 * transfer creation time -- both are re-read fresh, right before the
 * transfer is allowed to become the financial/inventory event.
 */
export async function approveTransfer(transfer, approverRoleLabel) {

  // ── FINAL APPROVAL: this is the actual financial/inventory event ──
  // Step 1: re-read current source balance fresh (Hard Block per approved decision)
  const { inventory: sourceInventory } = await loadProjectInventoryContext(transfer.from_project_id)
  const sourceByRef = {}
  sourceInventory.forEach(i => { sourceByRef[(i.ref_code || '').trim().toUpperCase()] = i })

  const insufficientLines = []
  for (const line of transfer.lines) {
    const key = (line.ref_code || '').trim().toUpperCase()
    const available = sourceByRef[key]?.balance ?? 0
    if (parseFloat(line.qty) > available) {
      insufficientLines.push({ ref_code: line.ref_code, requested: line.qty, available })
    }
  }
  if (insufficientLines.length > 0) {
    return {
      error: {
        message: `Insufficient stock — approval blocked: ` + insufficientLines
          .map(l => `${l.ref_code} (requested ${l.requested}, available ${l.available})`)
          .join('; ')
      },
      posted: false,
      insufficientLines,
    }
  }

  // Step 2: capture current source WAC fresh for every line (per approved decision)
  const lineUpdates = transfer.lines.map(line => {
    const key = (line.ref_code || '').trim().toUpperCase()
    const currentSourceWac = sourceByRef[key]?.weightedAvgRate ?? 0
    return { id: line.id, source_wac: currentSourceWac }
  })

  // Step 3: write both the line WAC captures and the header's final approval
  // status. NOT wrapped in a database transaction here (Supabase JS client
  // has no multi-statement transaction primitive) -- see "Atomicity" note
  // in the final report for the accepted risk and mitigation.
  for (const upd of lineUpdates) {
    await supabase.from('inventory_transfer_lines').update({ source_wac: upd.source_wac }).eq('id', upd.id)
  }

  const finalUpdate = { approval_stage: 'approved', status: 'approved', cc_approved_by: approverRoleLabel, cc_approved_at: new Date().toISOString() }

  const { error } = await supabase.from('inventory_transfers').update(finalUpdate).eq('id', transfer.id)
  return { error, posted: !error }
}

export async function rejectTransfer(transferId, reason) {
  return supabase.from('inventory_transfers')
    .update({ approval_stage: 'rejected', status: 'rejected', rejection_reason: reason, updated_at: new Date().toISOString() })
    .eq('id', transferId)
}

/**
 * receiveTransfer() — the third, distinct workflow role (2026-08-13):
 * "المخزن بيعمل ترانسفير والـ cost يوافق والمخزن بيستلم". Called by the
 * DESTINATION project's storekeeper (receive_transfer permission), only
 * available once a transfer has reached 'approved' status. This is the
 * ONLY action that credits the destination project's inventory/WAC --
 * loadProjectInventoryContext's Transfer-In query filters on
 * status='received' specifically, so approval alone has zero effect on
 * the destination (material is "in transit" between approval and this
 * confirmation).
 *
 * Guards against double-receiving: re-checks the transfer's current status
 * fresh before writing, refusing if it is already 'received' (or anything
 * other than 'approved') -- this is the "no possibility of orphan/duplicate
 * Transfer In" requirement from the original spec, applied to this second
 * status transition the same way approveTransfer() applies it to the first.
 */
export async function receiveTransfer(transferId, receiverLabel) {
  const { data: current, error: fetchErr } = await supabase
    .from('inventory_transfers').select('status').eq('id', transferId).single()
  if (fetchErr) return { error: fetchErr }
  if (current.status !== 'approved') {
    return { error: { message: `Cannot receive — transfer status is '${current.status}', expected 'approved'.` } }
  }
  const { error } = await supabase.from('inventory_transfers')
    .update({ status: 'received', received_by: receiverLabel, received_at: new Date().toISOString() })
    .eq('id', transferId)
    .eq('status', 'approved') // second guard: DB-level compare-and-swap, protects against a race
                               // between two storekeepers clicking "Receive" at the same moment
  return { error }
}
